import { motion } from 'framer-motion';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import heroImage from '../../assets/images_of_victoria/IMG_2253.jpg';
import supportImage from '../../assets/images_of_victoria/IMG_2280.jpg';
import { submitForm } from '../services/forms-api';

const pathways = [
  {
    title: 'Families',
    description: 'Ask about programs, support, upcoming events, or the best place to begin.',
  },
  {
    title: 'Volunteers',
    description: 'Reach out if you want to serve, show up, and support the mission in practical ways.',
  },
  {
    title: 'Partners',
    description: 'Community organizations, churches, and sponsors are welcome to start a conversation.',
  },
];

const messagePrompts = [
  'Program questions or family support needs',
  'Volunteer interest or event participation',
  'Partnership, sponsorship, or donation follow-up',
];

const nextSteps = [
  'Share a short note about what you need, what you are interested in, or how you would like to help.',
  "Leave the best email for follow-up so Victoria's Place can respond clearly and directly.",
  'If you are unsure where to start, that is fine. A simple message is enough.',
];

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setStatusMessage('');

    const result = await submitForm('contact', formData);

    if (result.success) {
      setStatus('success');
      setStatusMessage(result.message || 'Message sent successfully.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } else {
      setStatus('error');
      setStatusMessage(result.error || 'Failed to send message. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="contact-page">
      <section className="contact-hero contact-bleed">
        <div className="contact-hero-media">
          <img src={heroImage} alt="A joyful Victoria's Place moment" />
        </div>

        <motion.div
          className="contact-hero-inner"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <div className="contact-hero-copy">
            <span className="eyebrow contact-eyebrow">Contact</span>
            <p className="contact-kicker">Questions, volunteer interest, family support, and community partnerships</p>
            <h1>Reach out and we&apos;ll help you find the right next step.</h1>
            <p className="contact-hero-lead">
              Victoria&apos;s Place is here for families, supporters, and partners who want to connect with the mission,
              ask a question, or get involved in a meaningful way.
            </p>

            <div className="contact-hero-actions">
              <a href="#contact-form" className="button-primary tap-target">
                Send a message
              </a>
              <Link to="/programs" className="button-secondary tap-target">
                Explore programs
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      <div className="page-shell contact-shell">
        <motion.section
          className="contact-pathways"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.24 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          {pathways.map((item) => (
            <article key={item.title} className="contact-pathway">
              <p className="home-section-label">{item.title}</p>
              <p>{item.description}</p>
            </article>
          ))}
        </motion.section>

        <section className="contact-main-grid">
          <motion.div
            className="contact-story"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <div className="contact-story-copy">
              <p className="home-section-label">What To Share</p>
              <h2>This page should feel simple, warm, and easy to use.</h2>
              <p>
                Whether you are a parent looking for support, a volunteer wanting to help, or a partner interested in
                serving alongside Victoria&apos;s Place, this is the right place to start the conversation.
              </p>
              <div className="contact-prompt-list">
                {messagePrompts.map((item) => (
                  <div key={item} className="contact-prompt-item">
                    <span />
                    <p>{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <figure className="contact-story-photo">
              <img src={supportImage} alt="Child supported by the Victoria's Place mission" />
            </figure>
          </motion.div>

          <motion.section
            id="contact-form"
            className="contact-form-panel"
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.16 }}
            transition={{ duration: 0.48, ease: 'easeOut' }}
          >
            <div className="contact-form-heading">
              <p className="info-kicker">Send a message</p>
              <h2>Tell us what you need and we&apos;ll point you in the right direction.</h2>
              <p>
                A short message is enough. Share your name, the best email to reach you, and what you would like help
                with.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="contact-form">
              <div>
                <label htmlFor="name" className="field-label">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  className="field-input tap-target"
                  aria-required="true"
                />
              </div>

              <div>
                <label htmlFor="email" className="field-label">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="field-input tap-target"
                  aria-required="true"
                />
              </div>

              <div>
                <label htmlFor="subject" className="field-label">
                  Subject *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  className="field-input tap-target"
                  aria-required="true"
                />
              </div>

              <div>
                <label htmlFor="message" className="field-label">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleChange}
                  className="field-input tap-target resize-none"
                  aria-required="true"
                />
              </div>

              {statusMessage && (
                <div
                  className={`contact-status ${
                    status === 'success' ? 'contact-status--success' : 'contact-status--error'
                  }`}
                  role="alert"
                >
                  {statusMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="button-primary tap-target contact-submit-button disabled:cursor-not-allowed disabled:opacity-60"
              >
                {status === 'submitting' ? 'Sending...' : 'Send message'}
              </button>
            </form>
          </motion.section>
        </section>

        <motion.section
          className="contact-help-band"
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.22 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="contact-help-copy">
            <p className="home-section-label home-section-label--light">How It Works</p>
            <h2>If you are not sure what to write, start small.</h2>
          </div>

          <div className="contact-help-steps">
            {nextSteps.map((item, index) => (
              <div key={item} className="contact-help-step">
                <span>{`0${index + 1}`}</span>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </motion.section>
      </div>
    </div>
  );
}
